import React, { useMemo } from 'react';

import { QuestionError } from '@sm/question-definitions';
import {
  SingleQuestionController,
  ExternalQuestion,
  SingleQuestionControllerProps,
  RespondentDisplayOptions,
} from '@sm/question-widgets/respondent-survey';
import { SurveyFormatView } from '~app/components/Survey/SurveyFormat';
import groupQuestionsWithLayoutConfig from './utils';
import { QuestionRowLayout } from '../QuestionRowLayout';
import { registerValidator, unregisterValidator } from '../../validation';
import transformFromSurveyErrors from '../../errors/transformFromSurveyErrors';

import { useAppSelector } from '~app/hooks';
import { selectErrorsById } from '../../errors/errorsSlice';
import { selectAnswerById } from '../../surveySlice';

export type Props = {
  questions: ExternalQuestion[];
  /** Checks if the question coming in is OQAAT is default set to true */
  isOQAAT?: boolean;
};

type SingleQuestionControllerWrapperProps = Pick<SingleQuestionControllerProps, 'question'> &
  Omit<RespondentDisplayOptions, 'errors'>;

/** Wrapper to flatten `displayOptions` */
function SingleQuestionControllerWrapper({
  question,
  ...props
}: SingleQuestionControllerWrapperProps): React.ReactElement {
  const errors = useAppSelector(state => selectErrorsById(state, question.id));
  const answer = useAppSelector(state => selectAnswerById(state, question.id));

  const displayOptions = {
    errors: transformFromSurveyErrors<QuestionError>(errors),
    answer,
    registerValidator,
    unregisterValidator,
    ...props,
  };

  return <SingleQuestionController question={question} displayOptions={displayOptions} />;
}

function QuestionList(this: void, { questions, isOQAAT = true }: Props): React.ReactElement {
  const groups = useMemo(() => groupQuestionsWithLayoutConfig(questions), [questions]);

  return (
    <>
      {groups.map(group => (
        <QuestionRowLayout
          isOQAAT={isOQAAT}
          maxQuestionsPerRow={isOQAAT ? 1 : 4}
          key={`group_${group.map(g => g.id).concat('-')}`}
        >
          {group.map(question => (
            <SurveyFormatView name={question.id} key={question.id}>
              {/** `active` and `onSubmit` get automatically applied by `SurveyFormatView` */}
              <SingleQuestionControllerWrapper question={question} />
            </SurveyFormatView>
          ))}
        </QuestionRowLayout>
      ))}
    </>
  );
}

export default QuestionList;
